/**
 * The queue's saved views, and the query each one runs.
 *
 * Extracted from the page so it can be tested. The bug that prompted it: the
 * `queue-counts` endpoint scopes `breached` and `needs_routing` to OPEN states,
 * but the corresponding views did not — so the chip said 41 and the grid listed
 * closed tickets going back three years, because 6,900 of them breached at some
 * point in their life. `unassigned` and `mine` had the filter; those two were
 * simply forgotten, and nothing could catch it while the mapping lived inline
 * in JSX.
 *
 * The rule this file now encodes, and the tests enforce: **a view must select
 * the same population its badge counts.** A number next to a filter is a
 * promise about what clicking it will show.
 */

export type View =
  | 'open'
  | 'mine'
  | 'unassigned'
  | 'at_risk'
  | 'breached'
  | 'needs_routing'
  | 'blocked'
  | 'away'
  | 'all';

export interface ViewDef {
  value: View;
  label: string;
  title: string;
  /** The key in the queue-counts response whose number this view shows. */
  countKey: string;
}

export const VIEWS: ViewDef[] = [
  { value: 'open', label: 'Open', title: 'Everything not closed or cancelled', countKey: 'open' },
  { value: 'mine', label: 'Mine', title: 'Assigned to you', countKey: 'mine' },
  { value: 'unassigned', label: 'Unassigned', title: 'Nobody has picked these up', countKey: 'unassigned' },
  { value: 'at_risk', label: 'At risk', title: 'Inside the last stretch of the target', countKey: 'at_risk' },
  {
    value: 'breached',
    // "Breached" is ITSM's word and the security team reads it as a
    // security incident. The value and the URL keep the old spelling —
    // renaming those would break saved links for a wording problem.
    label: 'Overdue',
    title: 'Past the resolution target',
    countKey: 'breached',
  },
  { value: 'needs_routing', label: 'Needs routing', title: 'The router refused to guess', countKey: 'needs_routing' },
  { value: 'blocked', label: 'Blocked', title: 'Waiting on somebody outside the desk', countKey: 'blocked' },
  {
    value: 'away',
    label: 'Owner away',
    title: 'Open work held by somebody whose automatic replies say they are out',
    countKey: 'away',
  },
  { value: 'all', label: 'All', title: 'Including closed and cancelled', countKey: 'all' },
];

/** The open states, matching the OPEN_STATES list the server counts against. */
export const OPEN_STATES = [
  'new',
  'open',
  'pending_requester',
  'pending_external',
  'pending_internal',
  'pending_event',
] as const;

/** Views that must be restricted to live work to match their own count. */
export const OPEN_SCOPED: ReadonlySet<View> = new Set<View>([
  'open',
  'mine',
  'unassigned',
  'breached',
  'needs_routing',
  'away',
]);

export interface ViewQueryOptions {
  view: View;
  /** Only tickets carrying this tag. */
  tag?: string | null;
  sort: string;
  /** Board shows Resolved as a column, so it asks for states explicitly. */
  mode: 'list' | 'board';
  /** Needed by the "Mine" view; absent until /me resolves. */
  meOid?: string | null;
  /**
   * Somebody else's queue, by oid.
   *
   * Independent of `view`, deliberately: "Sam's overdue tickets" and "Sam's
   * blocked ones" are both questions a lead asks, and making this a view of
   * its own would mean choosing between the person and the filter. It
   * narrows whatever view is open instead.
   *
   * The API has taken `assigneeOid` since the beginning and nothing ever
   * sent it except the Mine view, so a lead could see on the reports screen
   * that somebody was holding forty tickets and had no way to open them.
   */
  assigneeOid?: string | null;
  queueKey?: string | null;
  requestType?: string | null;
  /** Keyset position: everything after this row, in the current sort. */
  cursor?: string | null;
}

/**
 * Build the ticket-list query for a view.
 *
 * Pure and total: same inputs, same URLSearchParams, no reads of anything
 * outside its arguments.
 */
export function queryForView(opts: ViewQueryOptions): URLSearchParams {
  const { view, sort, mode, meOid, queueKey, requestType, cursor, assigneeOid, tag } = opts;
  const q = new URLSearchParams({ limit: mode === 'board' ? '200' : '80', sort });
  if (cursor) q.set('cursor', cursor);

  if (mode === 'board' && view === 'open') {
    // The board shows Resolved as a column and as a drop target, so an
    // open-only filter would leave it permanently empty.
    for (const st of [...OPEN_STATES, 'resolved']) q.append('state', st);
  } else if (OPEN_SCOPED.has(view)) {
    q.set('open', 'true');
  }

  if (view === 'mine' && meOid) q.set('assigneeOid', meOid);
  if (view === 'unassigned') q.set('unassigned', 'true');
  if (view === 'at_risk') q.set('slaRisk', 'at_risk');
  if (view === 'breached') q.set('slaRisk', 'breached');
  if (view === 'needs_routing') q.set('needsHumanRouting', 'true');
  if (view === 'away') q.set('assigneeAway', 'true');
  if (view === 'blocked') {
    // A parked ticket is blocked on something outside the desk too — the
    // difference is only that this one has a date and brings itself back.
    // Leaving it out makes Blocked the one view that cannot find it.
    for (const s of ['pending_external', 'pending_internal', 'pending_requester', 'pending_event']) {
      q.append('state', s);
    }
  }

  /**
   * AFTER the view's own filters, and it wins.
   *
   * `unassigned` and a named assignee are contradictory — asking for both
   * returns nothing, which reads as "Sam has no tickets" rather than "that
   * question does not make sense". Naming a person clears it.
   */
  if (assigneeOid) {
    q.delete('unassigned');
    q.set('assigneeOid', assigneeOid);
  }

  if (queueKey) q.set('queueKey', queueKey);
  if (requestType) q.set('requestType', requestType);
  if (tag) q.set('tag', tag);
  return q;
}
